import React, { useEffect, useMemo, useState } from 'react';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { formatAnnouncementDate } from '../../utilities/format-date-helper-function';
import CardComponent from './../StandardCard';
import HarrisPagination from './../Pagination';

const AnnouncementsListComponent = (props) => {
  const { fields, sitecoreContext } = props;

  const [announcements, setAnnouncements] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [resultsCount, setResultsCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isExperienceEditor = sitecoreContext?.pageEditing === true;
  const language = sitecoreContext?.language || 'en-US';

  const pageSize = useMemo(() => {
    const size = parseInt(fields?.PageSize?.value, 10);
    return Number.isNaN(size) || size <= 0 ? 10 : size;
  }, [fields?.PageSize?.value]);

  useEffect(() => {
    setPageNumber(1);
  }, [pageSize]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const style = document.createElement('style');
    style.setAttribute('data-announcements-style-fix', 'true');
    style.innerHTML = `
      .rwc-col-layout { 
        row-gap: 12px; 
      }
      .rwc-col-layout__col--span-8-desktop {
        grid-column: span 12/span 12;
      }
      .rwc-col-layout__col > :not([hidden]) ~ :not([hidden]) {
        margin-top: calc(2rem * (1 - var(--tw-space-y-reverse)));
      }
      p { 
        margin-bottom: 0px; 
      }
      .rwc-col-layout__title { 
        margin-bottom: 0px !important;
        font-size: 38px !important;
        line-height: 45px !important;
        font-weight: 600 !important;
        word-wrap: break-word;
        overflow-wrap: break-word;
        hyphens: manual;
      }

      .rotaryui-card__section{
        gap: 10px;
      }

      [data-component="AnnouncementsList"] .announcements-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 24px;
      }

      [data-component="AnnouncementsList"] .rotaryui-org-pagination {
        margin-top: 32px !important;
      }

      [data-component="AnnouncementsList"] .rotaryui-card__visual:empty {
        display: none;
      }

      @media (min-width: 768px) {
        .rwc-col-layout__title {
          max-width: none;
          font-size: 48px !important;
          line-height: 57px !important;
          font-weight: 600 !important;
        }
        .rwc-col-layout__col--span-8-desktop .rwc-rich-text p {
          max-width: 604px;
        }
        [data-component="AnnouncementsList"] .announcements-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      @media (min-width: 1024px) {
        .rwc-col-layout__title {
          font-size: 58px !important;
          line-height: 70px !important;
          font-weight: 600 !important;
        }
        .rwc-col-layout__col--span-8-desktop .rwc-rich-text p {
          max-width: 618px;
        }
      }
    `;

    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (isExperienceEditor) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const apiBaseUrl = fields?.ApiUrl?.value;
        if (!apiBaseUrl) throw new Error('API URL missing');

        const apiUrl = `${apiBaseUrl}?pageSize=${pageSize}&pageNumber=${pageNumber}&language=${language}`;
        const response = await fetch(apiUrl);

        if (!response.ok) throw new Error(`API error ${response.status}`);

        const data = await response.json();
        setAnnouncements(data?.Results || []);
        setResultsCount(data?.ResultsCount || 0);
      } catch {
        setError('Failed to load announcements.');
        setAnnouncements([]);
        setResultsCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [pageNumber, pageSize, language, isExperienceEditor, fields?.ApiUrl?.value]);

  const totalPages = Math.ceil(resultsCount / pageSize);

  const handlePageChange = (newPage) => {
    if (newPage !== pageNumber) {
      setPageNumber(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const mapToCardFields = (item) => ({
  Title: { value: item.Title ?? '' },
  Date: { value: item.PublishDate ? formatAnnouncementDate(item.PublishDate) : '' },
  Url: {
    value: {
      href: item.Url || '#',
      linktype: 'external',
    },
  },
});

  if (isExperienceEditor) {
    return (
      <div style={{ padding: 16, border: '1px dashed #ccc', background: '#fafafa' }}>
        <p>Announcements load from API on the Page.</p>
      </div>
    );
  }

  return (
    <section className="announcements-container max-w-7xl mx-auto px-4 py-8" data-component="AnnouncementsList">
      {error && (
        <div className="bg-red-50 border border-red-300 p-4 rounded mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="spinner" />
        </div>
      )}

      {!isLoading && announcements.length > 0 && (
        <>
          <div className="announcements-grid">
            {announcements.map((item, index) => (
              <CardComponent key={item.Id || item.ItemId || index} fields={mapToCardFields(item)} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <HarrisPagination
                totalCount={resultsCount}
                pageSize={pageSize}
                currentPage={pageNumber}
                siblingCount={1}
                lang={language}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default withSitecoreContext()(AnnouncementsListComponent);
